// The selection controller every selection part shares. Selection itself lives in the table state and
// changes through dispatch with reason 'selection'; what the state does not hold (the shift-click anchor,
// the running bulk work, its outcome notice, a changed eligible count) lives in one small store per table
// instance, so the row cells, the header checkbox and the bulk bar read the same values.

import { useRef, useSyncExternalStore } from 'react'

import { useTable } from '../context'
import { ACTIONS } from '../types'
import { focusAfterFrame } from '../states/focusTarget'
import { FAILURE_TEXT, changedText } from './copy'
import {
  canEscalate,
  eligibleIds,
  escalate,
  headerState,
  isActive,
  isEligible,
  isQueryScoped,
  isSelected,
  noSelection,
  outcomeOf,
  rowId,
  selectRange,
  selectedCount,
  snapshotOf,
  toggleRow,
  togglePage,
} from './selection'

/**
 * @typedef {object} LocalSelection
 * @property {?string} anchor the row a shift-click range starts from
 * @property {?object} pending the snapshot of the running bulk work
 * @property {?{kind: string, text: string}} notice the latest outcome or changed-count message
 * @property {?{from: number, to: number}} changed the eligible count the consumer last reported
 */

/** @type {LocalSelection} */
const EMPTY = Object.freeze({
  anchor: null,
  pending: null,
  notice: null,
  changed: null,
})

const createStore = () => {
  let value = EMPTY
  const listeners = new Set()
  return {
    get: () => value,
    set: patch => {
      value = { ...value, ...patch }
      listeners.forEach(listener => listener())
    },
    subscribe: listener => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}

const stores = new WeakMap()

/** The store of one table instance, keyed by its unit ref (or its config where a test supplies no refs). */
export const storeFor = table => {
  const key = (table.refs && table.refs.unit) || table.config
  let store = stores.get(key)
  if (!store) {
    store = createStore()
    stores.set(key, store)
  }
  return store
}

/**
 * What the shell reads from one table's selection store before its context exists: whether bulk work is
 * running, and the eligible count a validation last reported.
 * @param {{current: ?HTMLElement}} unitRef
 * @returns {{pending: boolean, changedTotal: () => ?number}}
 */
export const useSelectionWork = unitRef => {
  const store = storeFor({ refs: { unit: unitRef } })
  const pending = useSyncExternalStore(
    store.subscribe,
    () => Boolean(store.get().pending),
    () => Boolean(store.get().pending)
  )
  return {
    pending,
    changedTotal: () => {
      const { changed } = store.get()
      return changed ? changed.to : null
    },
  }
}

/**
 * Starts one bulk action against a snapshot of the current selection. Query-scoped work first asks the
 * consumer for the current eligible count when the action declares validate(snapshot); a different count
 * replaces the selection's count and asks for another activation. Success and a partial outcome clear the
 * selection and refresh the query; failure keeps both. The returned promise rejects unless work completed,
 * so a confirming control can show its failure state.
 */
const runAction = async (store, latest, action) => {
  const { table, selection, eligibleTotal } = latest.current
  if (store.get().pending) throw new Error('pending')
  const snapshot = snapshotOf(selection, table.state.query, eligibleTotal)
  if (!snapshot.count) throw new Error('empty')
  store.set({ pending: snapshot, notice: null })

  if (snapshot.scope === 'all' && typeof action.validate === 'function') {
    let current
    try {
      current = await action.validate(snapshot)
    } catch (error) {
      store.set({
        pending: null,
        notice: { kind: 'failure', text: FAILURE_TEXT },
      })
      throw error
    }
    if (Number.isInteger(current) && current !== snapshot.count) {
      store.set({
        pending: null,
        changed: { from: snapshot.count, to: current },
        notice: {
          kind: 'changed',
          text: changedText(snapshot.count, current),
        },
      })
      // the selection now stands for the reported count, so it publishes again with that count
      latest.current.table.dispatch({
        type: ACTIONS.selectionSet,
        selection: { ...latest.current.selection },
        reason: 'selection',
      })
      throw new Error('changed')
    }
  }

  const settled = await Promise.resolve()
    .then(() => action.onSelect(snapshot))
    .then(
      value => ({ status: 'fulfilled', value }),
      error => ({ status: 'rejected', error })
    )
  const outcome = outcomeOf(settled, snapshot.count)
  store.set({ pending: null, notice: outcome, anchor: null })
  if (outcome.kind === 'failure') throw new Error(outcome.text)

  const now = latest.current.table
  now.dispatch({
    type: ACTIONS.selectionSet,
    selection: noSelection(),
    reason: 'selection',
  })
  store.set({ changed: null })
  if (outcome.kind === 'success' && now.refs && now.refs.title)
    focusAfterFrame(now.refs.title)
  if (typeof now.retry === 'function') now.retry()
  return outcome
}

/**
 * @returns {object} the current selection, its derived values and its operations
 */
export const useSelection = () => {
  const table = useTable()
  const { config, state, derived, dispatch } = table
  const store = storeFor(table)
  const local = useSyncExternalStore(store.subscribe, store.get, store.get)

  const selection = state.selection || noSelection()
  const queryScoped = isQueryScoped(selection)
  const pageRows = derived.pageRows || []
  const pageIds = eligibleIds(config, pageRows)
  const changed = queryScoped ? local.changed : null
  const eligibleTotal = changed ? changed.to : derived.eligibleTotal
  const count = selectedCount(selection, eligibleTotal)
  const pending = local.pending

  const latest = useRef(null)
  latest.current = { table, selection, eligibleTotal }

  const commit = next => {
    if (!isActive(selection) && isActive(next)) store.set({ notice: null })
    dispatch({
      type: ACTIONS.selectionSet,
      selection: next,
      reason: 'selection',
    })
  }

  return {
    selection,
    active: isActive(selection),
    queryScoped,
    pending,
    notice: local.notice,
    count,
    eligibleTotal,
    pageIds,
    header: headerState(selection, pageIds),
    canEscalate: canEscalate(selection, pageIds, eligibleTotal),
    isEligible: row => isEligible(config, row),
    isSelected: (id, row) => isSelected(config, selection, id, row),

    toggleRow: (row, index, shift) => {
      if (pending || queryScoped || !isEligible(config, row)) return
      const id = rowId(config, row)
      const from =
        local.anchor === null
          ? -1
          : pageRows.findIndex(other => rowId(config, other) === local.anchor)
      store.set({ anchor: id })
      commit(
        shift && from >= 0
          ? selectRange(config, selection, pageRows, from, index)
          : toggleRow(selection, id)
      )
    },
    togglePage: () => {
      if (pending || queryScoped || !pageIds.length) return
      store.set({ anchor: null })
      commit(togglePage(selection, pageIds))
    },
    escalate: () => {
      if (pending) return
      store.set({ anchor: null, changed: null })
      commit(escalate())
    },
    clear: () => {
      if (pending) return
      store.set({ anchor: null, changed: null })
      commit(noSelection())
    },
    run: action => runAction(store, latest, action),
  }
}
